'use client';

import { FC, useEffect } from 'react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui'

export interface SelectControlProps {
  /**
   * The currently selected value
   */
  value?: string

  /**
   * Called when the selection changes
   */
  onChange?: (value: string) => void

  /**
   * Label to display above the selector
   */
  label?: string

  /**
   * Placeholder text when no option is selected
   */
  placeholder?: string

  /**
   * Available options to select from
   */
  options: Array<{ label: string; value: string }>

  /**
   * Default value to select if none is provided
   */
  defaultValue?: string

  /**
   * Whether the selector is required
   */
  required?: boolean

  /**
   * Whether the selector is disabled
   */
  disabled?: boolean

  /**
   * Additional CSS classes to apply to the root element
   */
  className?: string
}

export const SelectControl: FC<SelectControlProps> = ({
  value,
  onChange,
  label,
  placeholder = 'Select option...',
  options,
  defaultValue,
  required,
  disabled,
  className = ''
}) => {
  const currentValue = value ?? defaultValue ?? ''

  // Auto-select if there's only one option and no value set
  useEffect(() => {
    if (options.length === 1 && !currentValue && options[0]?.value && onChange) {
      onChange(options[0].value)
    }
  }, [options, currentValue, onChange])

  // Find the selected option label
  const selectedOption = options.find(opt => opt.value === currentValue)

  return (
    <div className={`space-y-2 ${className}`} data-testid="select-control">
      {label && (
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium leading-none">
            {label}
          </label>
          {required && (
            <span className="text-red-500 text-xs leading-none">*</span>
          )}
        </div>
      )}
      <Select 
        value={currentValue} 
        onValueChange={onChange}
        disabled={disabled || options.length === 0}
      >
        <SelectTrigger className="w-full h-9 transition-all hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-h-[280px]">
          {options.map(option => (
            <SelectItem 
              key={option.value} 
              value={option.value}
              className="focus:bg-primary/10 hover:bg-muted/50 transition-colors"
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {currentValue && selectedOption && (
        <div className="text-xs text-muted-foreground mt-1">
          Selected: {selectedOption.label}
        </div>
      )}
    </div>
  )
} 